import { ListItem, IconButton, Stack } from '@mui/material';
import { TrashIcon } from '@sanity/icons';
import { Draggable } from 'react-beautiful-dnd';
import { PropsWithChildren, useMemo } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

interface Props {
  removeButtonLabel: string;
  onRemove: () => void;
  inputSize: 'small' | 'medium';
  inputMargin: 'none' | 'dense' | 'normal';
  index?: number;
  draggableId: string;
  enableDragAndDrop: boolean;
}

/**
 * DraggableFieldArrayItem is to be used as the child of DraggableFieldArray component
 * @param inputSize - Must be set equal to the size of child input field
 * @param inputMargin - Must be set equal to the margin of child input field
 */

export default function DraggableFieldArrayItem({
  removeButtonLabel,
  onRemove,
  children,
  inputSize,
  inputMargin,
  index,
  draggableId,
  enableDragAndDrop,
}: PropsWithChildren<Props>) {
  const marginTop = useMemo(() => getMarginTop(inputSize, inputMargin), [inputMargin, inputSize]);

  const content = (
    <ListItem
      disableGutters
      disablePadding
      sx={{
        alignItems: 'flex-start',
        ':not(:last-of-type)': { mb: 1 },
        gap: 0.5,
      }}
    >
      {enableDragAndDrop && (
        <IconButton
          size="small"
          sx={{
            mt: marginTop,
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            color: 'text.secondary',
          }}
          disableRipple
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      )}
      {children}
      <IconButton
        aria-label={removeButtonLabel}
        onClick={() => onRemove()}
        size={inputSize === 'small' ? 'medium' : 'large'}
        sx={{ mt: marginTop }}
      >
        <TrashIcon />
      </IconButton>
    </ListItem>
  );

  const shouldWrapInDraggable = enableDragAndDrop && draggableId && index !== undefined;

  if (!shouldWrapInDraggable) {
    return content;
  }

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(provided, snapshot) => (
        <Stack
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={{
            ...provided.draggableProps.style,
            opacity: snapshot.isDragging ? 0.8 : 1,
          }}
        >
          {content}
        </Stack>
      )}
    </Draggable>
  );
}

const getMarginTop = (inputSize: Props['inputSize'], inputMargin: Props['inputMargin']) => {
  return () => {
    if (inputSize === 'small') {
      switch (inputMargin) {
        case 'none':
          return 0;
        case 'dense':
          return 1;
        case 'normal':
          return 2;
        default:
          return 0.5;
      }
    }

    switch (inputMargin) {
      case 'none':
        return 0.25;
      case 'dense':
        return 1.25;
      case 'normal':
        return 2.25;
      default:
        return 0.5;
    }
  };
};
